import { addDays, format, isValid, parseISO, subDays } from 'date-fns';
import {
  CandidateFilterTypeKey,
  CandidateFilterCondition,
  CandidateFilterGroups,
  CandidateFilterMatchMode,
  CandidateFilterNode,
  CandidateFilterState,
  CandidateFilterValueType,
  CandidateRangeFilter,
  CandidateTextArrayFilter
} from 'modules/candidate-profile/types/CandidateSearchFilter';
import {
  CandidateFilterActiveModes,
  conditionId,
  isRange,
  isSelection,
  sameValues,
  withFreeIds
} from 'modules/candidate-profile/tables/filters/candidateFilterState';
import { canonicalFilterField } from 'modules/candidate-profile/tables/filters/filterFieldAliases';

const MODE_FOR_OPERATOR: Record<
  keyof CandidateTextArrayFilter,
  CandidateFilterMatchMode
> = {
  containsAny: CandidateFilterMatchMode.INCLUDE_ANY,
  containsAll: CandidateFilterMatchMode.INCLUDE_ALL,
  containsNone: CandidateFilterMatchMode.EXCLUDE
};

export type ParsedCandidateFilterTree = {
  conditions: CandidateFilterState[];
  activeModes: CandidateFilterActiveModes;
  groups: CandidateFilterGroups;
  /**
   * Conditions the filter panel cannot represent, so the restored panel is
   * narrower than the search that ran. Callers surface this rather than
   * pretending the restore was complete.
   */
  unsupportedCount: number;
};

export const isCondition = (
  node: CandidateFilterNode
): node is CandidateFilterCondition =>
  'field' in node &&
  typeof (node as CandidateFilterCondition).field === 'string';

const nonEmpty = (values: unknown): values is string[] =>
  Array.isArray(values) && values.length > 0;

const narrower = (
  a: number | string | undefined,
  b: number | string | undefined,
  keep: 'lower' | 'upper'
) => {
  if (a === undefined) return b;
  if (b === undefined) return a;
  return (keep === 'lower' ? a > b : a < b) ? a : b;
};

// Everything collected is AND-ed, so two ranges on one field are two halves of
// one constraint and the tighter bound at each end holds. Null when they cross,
// which nothing satisfies.
const intersectRanges = (
  existing: CandidateRangeFilter,
  incoming: CandidateRangeFilter
): CandidateRangeFilter | null => {
  const min = narrower(existing.min, incoming.min, 'lower');
  const max = narrower(existing.max, incoming.max, 'upper');
  if (min !== undefined && max !== undefined && min > max) return null;

  return { ...existing, min, max };
};

const shiftCalendarDayByOne = (
  value: string | undefined,
  shift: typeof addDays
): string | undefined => {
  const date = value === undefined ? null : parseISO(value.slice(0, 10));
  return date && isValid(date)
    ? format(shift(date, 1), 'yyyy-MM-dd')
    : undefined;
};

const pickNarrowerMinOrMax = (
  minMax: (string | undefined)[],
  keep: 'lower' | 'upper'
): string | undefined =>
  minMax.reduce<string | undefined>(
    (best, each) => narrower(best, each, keep) as string | undefined,
    undefined
  );

const typeKeyOf = (
  condition: CandidateFilterCondition
): CandidateFilterTypeKey | undefined =>
  Object.values(CandidateFilterTypeKey).find(
    (key) => condition[key] !== undefined
  );

/**
 * One condition -> the filter state that would rebuild it. Null when the shape
 * has no equivalent in the panel.
 *
 * `text` and `textArray` both collapse to a selection: the panel offers one
 * control per field and `buildCandidateFilterTree` picks the operator from the
 * field's type, so the type is read back off whichever type key is present
 * rather than from a field registry the panel does not have.
 *
 * A control holds one operator. A condition with two -- an include beside an
 * exclude, an exact number beside a bound -- would come back as one of them,
 * a wider search than the one that ran, so it has no equivalent either.
 */
const toFilterState = (
  condition: CandidateFilterCondition
): CandidateFilterState | null => {
  const { vectorDbKey } = condition;
  const field = canonicalFilterField(condition.field, typeKeyOf(condition));
  const key = vectorDbKey ? { vectorDbKey } : {};

  const textArray = condition.textArray;
  if (textArray) {
    const operators = (
      Object.keys(MODE_FOR_OPERATOR) as (keyof CandidateTextArrayFilter)[]
    ).filter((name) => nonEmpty(textArray[name]));
    if (operators.length !== 1) return null;
    const [operator] = operators;

    const mode = MODE_FOR_OPERATOR[operator];
    return {
      id: conditionId(field, mode),
      field,
      ...key,
      type: CandidateFilterValueType.TEXT_ARRAY,
      mode,
      values: textArray[operator] as string[]
    };
  }

  const text = condition.text;
  if (text) {
    // containsAll is unsatisfiable on a scalar, so the forward build never
    // emits it and Include all cannot be read back here.
    const exclude = nonEmpty(text.containsNone);
    const included: string[] = [];
    if (text.equal) included.push(text.equal);
    if (text.like) included.push(text.like);
    included.push(...(text.likeAny ?? []), ...(text.containsAny ?? []));
    if (exclude && included.length) return null;
    const values = exclude ? (text.containsNone as string[]) : included;
    if (!values.length) return null;

    const mode = exclude
      ? CandidateFilterMatchMode.EXCLUDE
      : CandidateFilterMatchMode.INCLUDE_ANY;
    return {
      id: conditionId(field, mode),
      field,
      ...key,
      type: CandidateFilterValueType.TEXT,
      mode,
      values
    };
  }

  const number = condition.number;
  if (number) {
    const hasBound =
      number.greaterOrEqual !== undefined || number.lessOrEqual !== undefined;
    if (number.equal !== undefined && hasBound) return null;
    if (number.equal === undefined && !hasBound) return null;

    // An exact number is the range from it to itself.
    return {
      id: field,
      field,
      ...key,
      type: CandidateFilterValueType.NUMBER,
      min: number.equal ?? number.greaterOrEqual,
      max: number.equal ?? number.lessOrEqual
    };
  }

  const date = condition.date;
  if (date) {
    // The panel's range is inclusive, so a strict min or max moves one calendar day in.
    const min = pickNarrowerMinOrMax(
      [
        date.greaterOrEqual,
        date.equal,
        shiftCalendarDayByOne(date.greaterThan, addDays)
      ],
      'lower'
    );
    const max = pickNarrowerMinOrMax(
      [
        date.lessOrEqual,
        date.equal,
        shiftCalendarDayByOne(date.lessThan, subDays)
      ],
      'upper'
    );
    if (min === undefined && max === undefined) return null;
    return {
      id: field,
      field,
      ...key,
      type: CandidateFilterValueType.DATE,
      min,
      max
    };
  }

  const boolean = condition.boolean;
  if (boolean && typeof boolean.equal === 'boolean') {
    return {
      id: field,
      field,
      ...key,
      type: CandidateFilterValueType.BOOLEAN,
      value: boolean.equal
    };
  }

  const geo = condition.geo;
  if (geo) {
    return {
      id: field,
      field,
      type: CandidateFilterValueType.GEO,
      latitude: geo.latitude,
      longitude: geo.longitude,
      // Miles, the unit the contract uses. The service converts to metres.
      radiusMiles: geo.radiusMiles,
      ...(geo.name ? { name: geo.name } : {})
    };
  }

  return null;
};

type CollectedConditions = {
  conditions: CandidateFilterState[];
  groups: CandidateFilterState[][];
  unsupportedCount: number;
};

const collectConditions = (node: CandidateFilterNode): CollectedConditions => {
  if (isCondition(node)) {
    const condition = toFilterState(node);
    return condition
      ? { conditions: [condition], groups: [], unsupportedCount: 0 }
      : { conditions: [], groups: [], unsupportedCount: 1 };
  }

  // Nested `and` is flattened: the stored tree AND-s the recruiter's tree with
  // the generated one, so the recruiter's own `and` arrives one level down.
  if (node.and) {
    const children = node.and.map(collectConditions);
    return {
      conditions: children.flatMap((child) => child.conditions),
      groups: children.flatMap((child) => child.groups),
      unsupportedCount: children.reduce(
        (total, child) => total + child.unsupportedCount,
        0
      )
    };
  }

  if (node.or) {
    // A nested `or` is the same Or.
    const arms = node.or.filter((child) => isCondition(child) || child.or);
    const children = arms.map(collectConditions);

    // Restoring part of an `or` would change what the filter means.
    const unsupported =
      arms.length !== node.or.length ||
      children.some((child) => child.unsupportedCount > 0);
    if (unsupported) {
      return { conditions: [], groups: [], unsupportedCount: node.or.length };
    }

    const group = children.flatMap((child) => child.conditions);
    return {
      conditions: group,
      groups: group.length > 1 ? [group] : [],
      unsupportedCount: 0
    };
  }

  if (node.not) {
    return { conditions: [], groups: [], unsupportedCount: 1 };
  }

  return { conditions: [], groups: [], unsupportedCount: 0 };
};

/**
 * Add `condition` to `conditions`, combining it with any condition already
 * there under the same id. False when the two cannot be combined.
 *
 * Both halves of the AND can constrain one field in one mode -- the recruiter
 * filtering on a skill the model also asked for. `contains any of [a]` AND
 * `contains any of [b]` is not `contains any of [a, b]`, so they stay two
 * conditions and each gets a chip. An exact repeat is added once. Two ranges
 * intersect. Anything else -- two radii, a range beside a selection -- cannot.
 * A group member is one arm of an or and never combines: `(A or B) and A` is A.
 */
const addCondition = (
  conditions: CandidateFilterState[],
  condition: CandidateFilterState,
  inGroup: Set<CandidateFilterState>
): boolean => {
  if (inGroup.has(condition)) {
    conditions.push(condition);
    return true;
  }

  const existing = conditions.filter(
    (each) => each.id === condition.id && !inGroup.has(each)
  );
  if (!existing.length) {
    conditions.push(condition);
    return true;
  }

  if (isRange(condition)) {
    const [range] = existing;
    if (existing.length > 1 || !isRange(range)) return false;
    if (range.type !== condition.type) return false;

    const intersection = intersectRanges(range, condition);
    if (!intersection) return false;

    conditions[conditions.indexOf(range)] = intersection;
    return true;
  }

  if (!isSelection(condition) || !existing.every(isSelection)) return false;

  const isRepeat = existing.some((each) =>
    sameValues(each.values, condition.values)
  );
  if (!isRepeat) conditions.push(condition);
  return true;
};

/**
 * A restored field shows the mode it was saved with, so the control matches the
 * values beside it. A field with both an include and an exclude keeps the
 * first, which is the one `toTableFilterValues` renders.
 */
export const activeModesForConditions = (
  conditions: CandidateFilterState[]
): CandidateFilterActiveModes => {
  const activeModes: CandidateFilterActiveModes = {};

  conditions.forEach((condition) => {
    if ('mode' in condition && !(condition.field in activeModes)) {
      activeModes[condition.field] = condition.mode;
    }
  });

  return activeModes;
};

/**
 * The stored filter tree -> sourcing filter state, the inverse of
 * `buildCandidateFilterTree`. Used when reopening a saved search, where the
 * tree is the only record of what was applied.
 */
export const parseCandidateFilterTree = (
  tree: CandidateFilterNode | null | undefined
): ParsedCandidateFilterTree => {
  if (!tree) {
    return { conditions: [], activeModes: {}, groups: [], unsupportedCount: 0 };
  }

  const collected = collectConditions(tree);
  const inGroup = new Set(collected.groups.flat());

  const added: CandidateFilterState[] = [];
  let { unsupportedCount } = collected;
  collected.conditions.forEach((condition) => {
    if (!addCondition(added, condition, inGroup)) unsupportedCount += 1;
  });

  const conditions = withFreeIds(added, []);
  const idOf = new Map(
    added.map((condition, at) => [condition, conditions[at].id])
  );
  const groups = collected.groups.map((group) =>
    group.map((condition) => idOf.get(condition) as string)
  );

  return {
    conditions,
    groups,
    activeModes: activeModesForConditions(conditions),
    unsupportedCount
  };
};
